import { Signal, computed, signal } from '@angular/core';

import type { GrCanvasItem } from '../canvas/canvas.types';
import type {
  CanvasEditorApi,
  CanvasEditorStoreConfig,
  CanvasMinSizeMap,
} from './canvas-editor-store.types';
import {
  addItem,
  addItemAt,
  deleteRows,
  moveItem,
  normalize,
  removeItem,
  resizeItem,
} from './canvas-editor-grid';

export class CanvasEditorStore<TItem extends GrCanvasItem> implements CanvasEditorApi {
  private readonly config: CanvasEditorStoreConfig<TItem>;
  private readonly cols: number;

  private readonly _draft = signal<ReadonlyArray<TItem>>([]);
  private readonly _editing = signal<boolean>(false);
  private readonly _history = signal<ReadonlyArray<ReadonlyArray<TItem>>>([]);

  readonly editing = this._editing.asReadonly();
  readonly draftLayout = this._draft.asReadonly();
  readonly savedLayout: Signal<ReadonlyArray<TItem>>;
  readonly activeLayout: Signal<ReadonlyArray<TItem>>;
  readonly isDirty: Signal<boolean>;
  readonly canUndo: Signal<boolean>;
  readonly minSizeByKind: CanvasMinSizeMap;

  constructor(config: CanvasEditorStoreConfig<TItem>) {
    this.config = config;
    this.cols = config.cols ?? 12;
    this.savedLayout = computed(() =>
      normalize(this.clone(config.persistence.saved()), this.cols),
    );
    this._draft.set(this.savedLayout());
    this.activeLayout = computed(() =>
      this._editing() ? this._draft() : this.savedLayout(),
    );
    this.isDirty = computed(
      () => JSON.stringify(this._draft()) !== JSON.stringify(this.savedLayout()),
    );
    this.canUndo = computed(() => this._history().length > 0);
    this.minSizeByKind = Object.fromEntries(
      Object.entries(config.kindCatalog).map(([kind, size]) => [
        kind,
        { colSpan: size.minColSpan, rowSpan: size.minRowSpan },
      ]),
    );
  }

  enterEdit(): void {
    this._draft.set(this.savedLayout());
    this._history.set([]);
    this._editing.set(true);
  }

  exitEdit(): void {
    this._editing.set(false);
  }

  save(): void {
    this.config.persistence.persist(this.clone(this._draft()));
    this.exitEdit();
  }

  cancel(): void {
    this._draft.set(this.savedLayout());
    this._history.set([]);
    this.exitEdit();
  }

  reset(): void {
    this._history.set([]);
    this._draft.set(normalize(this.clone(this.config.persistence.defaults()), this.cols));
  }

  undo(): void {
    this._history.update((h) => {
      if (h.length === 0) return h;
      this._draft.set(h[h.length - 1] as TItem[]);
      return h.slice(0, -1);
    });
  }

  remove(id: string): void {
    this._history.update((h) => [...h, this._draft()]);
    this._draft.update((layout) => removeItem(layout, id, this.cols));
  }

  move(id: string, x: number, y: number): void {
    this._history.update((h) => [...h, this._draft()]);
    this._draft.update((layout) => moveItem(layout, id, x, y, this.cols));
  }

  resize(id: string, colSpan: number, rowSpan: number): void {
    this._history.update((h) => [...h, this._draft()]);
    this._draft.update((layout) => resizeItem(layout, id, colSpan, rowSpan, this.cols));
  }

  add(kind: string, config: unknown): TItem {
    const size = this.config.kindCatalog[kind];
    const item = {
      id: this.makeId(kind),
      kind,
      config: this.cloneValue(config),
      colSpan: size?.defaultColSpan ?? 4,
      rowSpan: size?.defaultRowSpan ?? 2,
    } as unknown as TItem;
    this._history.update((h) => [...h, this._draft()]);
    this._draft.update((layout) => addItem(layout, item, this.cols));
    return item;
  }

  addAt(
    kind: string,
    config: unknown,
    x: number,
    y: number,
    colSpan?: number,
    rowSpan?: number,
  ): TItem {
    const size = this.config.kindCatalog[kind];
    const item = {
      id: this.makeId(kind),
      kind,
      config: this.cloneValue(config),
      colSpan: colSpan ?? size?.defaultColSpan ?? 4,
      rowSpan: rowSpan ?? size?.defaultRowSpan ?? 2,
    } as unknown as TItem;
    this._history.update((h) => [...h, this._draft()]);
    this._draft.update((layout) => addItemAt(layout, item, x, y, this.cols));
    return item;
  }

  update(id: string, config: unknown): void {
    this._draft.update((layout) =>
      layout.map((w) => (w.id === id ? ({ ...w, config: this.cloneValue(config) } as TItem) : w)),
    );
  }

  deleteRows(startRow: number, count: number): void {
    this._history.update((h) => [...h, this._draft()]);
    this._draft.update((layout) => deleteRows(layout, startRow, count));
  }

  findById(id: string): TItem | undefined {
    return this._draft().find((w) => w.id === id);
  }

  private clone(layout: ReadonlyArray<TItem>): TItem[] {
    return layout.map((w) => this.cloneValue(w));
  }

  private cloneValue<T>(value: T): T {
    return typeof structuredClone === 'function'
      ? structuredClone(value)
      : (JSON.parse(JSON.stringify(value)) as T);
  }

  private makeId(kind: string): string {
    if (this.config.idFactory) return this.config.idFactory(kind);
    const rand = Math.random().toString(36).slice(2, 8);
    return `${kind}-${Date.now().toString(36)}-${rand}`;
  }
}
